/**
 * Signup form input helpers — UI sanitization & validation only.
 * Does not define API contracts; callers must keep RegisterRequestDto shape unchanged.
 */

import {
  countries,
  findCountryByLongestDialPrefix,
  getCountryByIso2,
  toDialCodeDigits,
  type CountryType,
} from "@/utils/country-codes";

export { toDialCodeDigits } from "@/utils/country-codes";

export const SIGNUP_NAME_MAX_LENGTH = 50;
export const SIGNUP_EMAIL_MAX_LENGTH = 254;
/** Existing register business rule (unchanged). */
export const SIGNUP_PASSWORD_MIN_LENGTH = 5;
/** Existing register business rule (unchanged). */
export const SIGNUP_PASSWORD_MAX_LENGTH = 25;
/** National (subscriber) number length — E.164-compatible range for local input. */
export const SIGNUP_MOBILE_MIN_DIGITS = 5;
export const SIGNUP_MOBILE_MAX_DIGITS = 15;
/** ITU E.164 maximum total digits (country code + national number). */
export const E164_MAX_DIGITS = 15;
/** Hard cap on raw mobile field length to stop paste abuse (formatting chars included). */
export const SIGNUP_MOBILE_INPUT_MAX_LENGTH = 24;

/** Practical email pattern — not full RFC. */
export const SIGNUP_EMAIL_PATTERN =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;

/** Unicode letters, spaces, hyphen, apostrophe (straight + curly). */
const PERSON_NAME_ALLOWED_RE = /^[\p{L}\s'\u2019-]+$/u;
const PERSON_NAME_CHAR_RE = /[\p{L}\s'\u2019-]/gu;
// Strip C0/C1 controls (intentional; not a match-anything pattern).
// eslint-disable-next-line no-control-regex -- sanitize pasted control characters from name fields
const CONTROL_CHARS_RE = /[\u0000-\u001F\u007F-\u009F]/g;
const MULTI_SPACE_RE = /\s+/g;

export function stripControlChars(value: string): string {
  return String(value ?? "").replace(CONTROL_CHARS_RE, "");
}

/** Trim, collapse internal whitespace, strip control characters. */
export function normalizePersonName(value: string): string {
  return stripControlChars(String(value ?? ""))
    .trim()
    .replace(MULTI_SPACE_RE, " ");
}

/**
 * Live input filter for first/last name: strip controls/invalid chars, enforce max length.
 * Does not trim trailing spaces while typing (trim happens on blur/submit).
 */
export function clampPersonNameInput(value: string): string {
  const cleaned = stripControlChars(String(value ?? ""));
  const allowed = cleaned.match(PERSON_NAME_CHAR_RE)?.join("") ?? "";
  return allowed.slice(0, SIGNUP_NAME_MAX_LENGTH);
}

export function isValidPersonName(value: string): boolean {
  const normalized = normalizePersonName(value);
  if (!normalized) return false;
  if (normalized.length > SIGNUP_NAME_MAX_LENGTH) return false;
  return PERSON_NAME_ALLOWED_RE.test(normalized);
}

export function normalizeEmailInput(value: string): string {
  return stripControlChars(String(value ?? "")).trim();
}

export function clampEmailInput(value: string): string {
  return stripControlChars(String(value ?? "")).slice(0, SIGNUP_EMAIL_MAX_LENGTH);
}

export function isValidEmail(value: string): boolean {
  const email = normalizeEmailInput(value);
  if (!email) return false;
  if (email.length > SIGNUP_EMAIL_MAX_LENGTH) return false;
  return SIGNUP_EMAIL_PATTERN.test(email);
}

/** Digits only count toward E.164 length (leading + ignored). */
export function countMobileDigits(value: string): number {
  return String(value ?? "").replace(/\D/g, "").length;
}

/** Digits-only extraction (control chars stripped first). */
export function digitsOnly(value: string): string {
  return stripControlChars(String(value ?? "")).replace(/\D/g, "");
}

/**
 * National mobile field (country dial selected separately): digits only.
 * Caps by national max and remaining E.164 budget given dial length.
 */
export function clampNationalMobileDigits(
  value: string,
  dialCodePhone: string | null | undefined = ""
): string {
  const dialLen = toDialCodeDigits(dialCodePhone).length;
  const maxNational =
    dialLen > 0
      ? Math.min(SIGNUP_MOBILE_MAX_DIGITS, Math.max(0, E164_MAX_DIGITS - dialLen))
      : SIGNUP_MOBILE_MAX_DIGITS;
  return digitsOnly(value).slice(0, maxNational);
}

/** Strip a single leading trunk `0` (e.g. UK/IN local format). */
export function stripLeadingTrunkZero(national: string): string {
  const d = digitsOnly(national);
  if (d.length >= 2 && d.startsWith("0")) return d.slice(1);
  return d;
}

/**
 * Normalize a national number for validation/submit:
 * digits only → strip duplicated dial → strip trunk 0 → length clamp.
 */
export function normalizeNationalNumber(
  value: string,
  dialCodePhone: string | null | undefined = ""
): string {
  let national = stripLeadingDialCodeFromNational(value, dialCodePhone);
  national = stripLeadingTrunkZero(national);
  return clampNationalMobileDigits(national, dialCodePhone);
}

export function isValidNationalMobile(
  value: string,
  dialCodePhone: string | null | undefined = ""
): boolean {
  const national = normalizeNationalNumber(value, dialCodePhone);
  if (
    national.length < SIGNUP_MOBILE_MIN_DIGITS ||
    national.length > SIGNUP_MOBILE_MAX_DIGITS
  ) {
    return false;
  }
  const dial = toDialCodeDigits(dialCodePhone);
  if (dial && dial.length + national.length > E164_MAX_DIGITS) return false;
  return true;
}

/** Alias used by address / shared phone UX. */
export const isValidNationalNumber = isValidNationalMobile;

/**
 * If the user pasted a full international number into the national field,
 * strip a matching leading dial code once (avoids +91 + 9198… duplication).
 */
export function stripLeadingDialCodeFromNational(
  nationalDigits: string,
  dialCodePhone: string | null | undefined
): string {
  const dial = toDialCodeDigits(dialCodePhone);
  const national = digitsOnly(nationalDigits);
  if (!dial || !national.startsWith(dial)) return national;
  const stripped = national.slice(dial.length);
  // Only strip when enough subscriber digits remain
  if (stripped.length >= SIGNUP_MOBILE_MIN_DIGITS) return stripped;
  return national;
}

/**
 * Build normalized E.164: "+" + dialDigits + nationalDigits.
 * Returns "" when inputs cannot form a valid E.164 number.
 */
export function buildE164Phone(
  dialCodePhone: string | null | undefined,
  nationalNumber: string | null | undefined
): string {
  const dial = toDialCodeDigits(dialCodePhone);
  const national = normalizeNationalNumber(String(nationalNumber ?? ""), dial);
  if (!dial || !isValidNationalMobile(national, dial)) return "";
  return `+${dial}${national}`;
}

/**
 * If contact is already E.164, return it; otherwise build from dial + national.
 * Used by checkout when stored address phones may be E.164 or local.
 */
export function resolveContactPhoneNumber(
  dialCodePhone: string | null | undefined,
  contactNo: string | null | undefined
): string {
  const normalized = normalizeMobileForSubmit(String(contactNo ?? ""));
  if (normalized && isValidE164Phone(normalized)) return normalized;
  const built = buildE164Phone(dialCodePhone, contactNo);
  if (built) return built;
  const dial = toDialCodeDigits(dialCodePhone);
  const national = digitsOnly(String(contactNo ?? ""));
  return dial || national ? `+${dial}${national}` : "";
}

export function isValidE164Phone(value: string): boolean {
  const raw = stripControlChars(String(value ?? "")).trim();
  if (!/^\+[1-9]\d{5,14}$/.test(raw)) return false;
  const digits = raw.slice(1);
  return digits.length >= 6 && digits.length <= E164_MAX_DIGITS;
}

export type ParsedStoredPhone = {
  country: CountryType | undefined;
  nationalNumber: string;
};

/**
 * Parse a stored phone (E.164 or local digits) for edit forms.
 * E.164 → longest dial match + national remainder.
 * Local digits → fallback country + digits as-is.
 */
export function parseStoredPhoneNumber(
  stored: string | null | undefined,
  fallbackCountry?: CountryType | null
): ParsedStoredPhone {
  const raw = stripControlChars(String(stored ?? "")).trim();
  if (!raw) {
    return { country: fallbackCountry ?? undefined, nationalNumber: "" };
  }

  const normalized = normalizeMobileForSubmit(raw);
  if (normalized.startsWith("+")) {
    const digits = normalized.slice(1);
    const matched = findCountryByLongestDialPrefix(digits);
    const country = matched ?? fallbackCountry ?? undefined;
    const dial = country ? toDialCodeDigits(country.phone) : "";
    const national =
      dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits;
    return { country, nationalNumber: national };
  }

  return {
    country: fallbackCountry ?? undefined,
    nationalNumber: digitsOnly(normalized),
  };
}

/** Resolve default phone country: address → restaurant ISO2 → locale ISO2 → GB. */
export function resolveDefaultPhoneCountry(args: {
  addressCountry?: CountryType | null;
  restaurantIso2?: string | null;
  localeIso2?: string | null;
}): CountryType {
  return (
    args.addressCountry ??
    getCountryByIso2(args.restaurantIso2) ??
    getCountryByIso2(args.localeIso2) ??
    getCountryByIso2("GB") ??
    countries[0]
  );
}

/**
 * Live free-text mobile input (login / legacy single-field): digits, optional +,
 * spaces, dashes, parentheses. Caps digit count at 15.
 */
export function clampMobileInput(value: string): string {
  let raw = stripControlChars(String(value ?? ""));
  raw = raw.replace(/[^\d+\s()-]/g, "");

  const hasPlus = raw.trimStart().startsWith("+");
  raw = raw.replace(/\+/g, "");
  if (hasPlus) raw = `+${raw}`;

  let digits = 0;
  let out = "";
  for (const ch of raw) {
    if (/\d/.test(ch)) {
      if (digits >= SIGNUP_MOBILE_MAX_DIGITS) continue;
      digits += 1;
    }
    out += ch;
    if (out.length >= SIGNUP_MOBILE_INPUT_MAX_LENGTH) break;
  }
  return out;
}

/**
 * Strip spaces/dashes/parentheses for validation/submit.
 * Preserves a single leading + when present.
 */
export function normalizeMobileForSubmit(value: string): string {
  const raw = stripControlChars(String(value ?? "")).trim();
  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return hasPlus ? `+${digits}` : digits;
}

/** True for national 5–15 digits or a valid E.164 string. */
export function isValidMobile(value: string): boolean {
  const normalized = normalizeMobileForSubmit(value);
  if (!normalized) return false;
  if (normalized.startsWith("+")) return isValidE164Phone(normalized);
  const digits = normalized.replace(/\D/g, "");
  return (
    digits.length >= SIGNUP_MOBILE_MIN_DIGITS &&
    digits.length <= SIGNUP_MOBILE_MAX_DIGITS
  );
}

/**
 * Login / forgot-password identifier: email, E.164, or national digits (5–15).
 * Keeps legacy 10-digit accounts working alongside new E.164 signups.
 */
export function isValidEmailOrMobileIdentifier(value: string): boolean {
  const raw = stripControlChars(String(value ?? "")).trim();
  if (!raw) return false;
  if (raw.includes("@")) return isValidEmail(raw);
  return isValidMobile(raw);
}

/** Normalize identifier for auth APIs without changing field shape. */
export function normalizeEmailOrMobileIdentifier(value: string): string {
  const raw = stripControlChars(String(value ?? "")).trim();
  if (!raw) return "";
  if (raw.includes("@")) return normalizeEmailInput(raw);
  return normalizeMobileForSubmit(raw);
}

export function clampPasswordInput(value: string): string {
  // Do not trim passwords; only strip null/control chars and enforce max length.
  return stripControlChars(String(value ?? "")).slice(0, SIGNUP_PASSWORD_MAX_LENGTH);
}

/** Birthday month options — label is human-readable, value is the API 2-digit month. */
export const BIRTHDAY_MONTH_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

/**
 * Days per month for a yearless birthday. February is 29 by contract: 02-29 is a
 * recurring birthday, so leap-year/current-year logic must never gate it.
 */
const BIRTHDAY_DAYS_IN_MONTH: Readonly<Record<string, number>> = {
  "01": 31,
  "02": 29,
  "03": 31,
  "04": 30,
  "05": 31,
  "06": 30,
  "07": 31,
  "08": 31,
  "09": 30,
  "10": 31,
  "11": 30,
  "12": 31,
};

export const BIRTHDAY_MAX_DAYS = 31;

/** Shape of the single frontend/backend birthday contract. */
export const BIRTHDAY_PATTERN = /^\d{2}-\d{2}$/;

export function isValidBirthdayMonth(month: string | null | undefined): boolean {
  return Object.prototype.hasOwnProperty.call(
    BIRTHDAY_DAYS_IN_MONTH,
    String(month ?? "")
  );
}

/** Day count for a month; 31 while no month is selected yet. */
export function birthdayDaysInMonth(month: string | null | undefined): number {
  return BIRTHDAY_DAYS_IN_MONTH[String(month ?? "")] ?? BIRTHDAY_MAX_DAYS;
}

/** "01".."31" clipped to the selected month (all 31 when no month is selected). */
export function birthdayDayOptions(month: string | null | undefined): string[] {
  return Array.from({ length: birthdayDaysInMonth(month) }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );
}

export function isValidBirthdayDayForMonth(
  month: string | null | undefined,
  day: string | null | undefined
): boolean {
  const raw = String(day ?? "");
  if (!/^\d{2}$/.test(raw)) return false;
  if (!isValidBirthdayMonth(month)) return false;
  const dayNumber = Number(raw);
  return dayNumber >= 1 && dayNumber <= birthdayDaysInMonth(month);
}

/** "MM-DD" from the two controls; "" until both are selected. */
export function buildBirthday(
  month: string | null | undefined,
  day: string | null | undefined
): string {
  const m = String(month ?? "").trim();
  const d = String(day ?? "").trim();
  if (!m || !d) return "";
  return `${m}-${d}`;
}

/**
 * Birthday contract check: MM-DD only — no year, no Date object, no age rule.
 * Accepts 02-29; rejects 04-31 / 06-31 / 09-31 / 11-31.
 * Returns true or a static error message (never echoes user input).
 */
export function validateSignupBirthday(value: string): true | string {
  const raw = String(value ?? "").trim();
  if (!raw) return "Birthday is required";
  if (!BIRTHDAY_PATTERN.test(raw)) return "Enter a valid birthday";

  const [month, day] = raw.split("-");
  if (!isValidBirthdayMonth(month)) return "Enter a valid birthday";
  if (!isValidBirthdayDayForMonth(month, day)) return "Enter a valid birthday";

  return true;
}
